import { createContext, useContext, useEffect, useMemo, useState } from 'react';

type Theme = 'high-contrast-light' | 'high-contrast-dark';

interface ThemeContextValue {
  theme: Theme;
  toggleTheme: () => void;
  setTheme: (t: Theme) => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

function getInitialTheme(): Theme {
  const stored =
    typeof window !== 'undefined' ? localStorage.getItem('theme') : null;
  if (stored === 'high-contrast-light' || stored === 'high-contrast-dark') {
    return stored;
  }
  const defaultTheme: Theme = 'high-contrast-dark';
  return defaultTheme;
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(getInitialTheme);
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    setThemeState(prev => {
      switch (prev) {
        case 'high-contrast-light': return 'high-contrast-dark';
        case 'high-contrast-dark': return 'high-contrast-light';
        default: return 'high-contrast-dark';
      }
    });
  };

  const setTheme = (t: Theme) => setThemeState(t);
  const value = useMemo(() => ({ theme, toggleTheme, setTheme }), [theme]);

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within a ThemeProvider');
  return ctx;
}
